import { Injectable } from "@angular/core";
import {firstValueFrom } from "rxjs";
import { HttpResult } from "../../core/services/base-http.service";
import {
  APIErrorModel,
  BaseResponse,
} from "../../shared/models/base-response.model";
import { InviteDetailsModel } from "./exh-invite.model";
import { ExhInviteApiService } from "./exh-invite-api.service";
import { StorageService } from "../../core/services/storage.service";

@Injectable({ providedIn: "root" })
export class ExhInviteBllService {
  /**
   * @param api      Service for making auth-related HTTP calls (exchange code, build URLs).
   * @param storage  Client-side storage for tokens, payloads, and PKCE verifiers.
   * @param router   Angular Router, used to redirect on logout.
   */
  constructor(
    private api: ExhInviteApiService,
    private storageService: StorageService
  ) {}
  /**
   * Retrieves the invitation details based on the provided invitation code.
   *
   * @param invitation_code - The unique code for the invitation to retrieve details for.
   * @returns A promise that resolves to a BaseResponse containing the invitation details.
   * @throws APIErrorModel if the API request fails or returns a non-success status.
   */
  async getInvitationDetails(
    invitation_code: string
  ): Promise<BaseResponse<InviteDetailsModel>> {
    // Await the API response for getting the user event status
    const result: HttpResult<BaseResponse<InviteDetailsModel>> =
      await firstValueFrom(this.api.getInvitationDetails(invitation_code));

    // Destructure status and body from the result
    const { status, body } = result;

    // If success, return the body
    if (status === 200) {
      return body;
    } else {
      // If not success, throw error with details
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  // Accept an invitation using the provided invitation code
  async acceptInvitation(invitation_code: string): Promise<BaseResponse<any>> {
    // Await the API response for getting the user event status
    const result: HttpResult<BaseResponse<any>> = await firstValueFrom(
      this.api.acceptInvitation(invitation_code)
    );

    const { status, body } = result;

    // If success, return the body
    if (status === 200) {
      return body;
    }
    // 409 Conflict condition here
    else if (status === 409) {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
    // Other errors
    else {
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  // Fetch the list of pending invitations
  async getPendingInviteList(): Promise<BaseResponse<InviteDetailsModel[]>> {
    // Await the API response for getting the user event status
    const result: HttpResult<BaseResponse<InviteDetailsModel[]>> =
      await firstValueFrom(this.api.getPendingInviteList());

    // Destructure status and body from the result
    const { status, body } = result;

    // If success, return the body
    if (status === 200) {
      return body;
    } else {
      // If not success, throw error with details
      throw <APIErrorModel>{
        status,
        message: body.message,
        data: body.data,
      };
    }
  }

  saveInviteAccepted(code: string): void {
    this.storageService.saveIsInviteAccepted(code);
  }
}
